import { ArrowFloorButton, FloorButton, SecondaryFloorButton } from '@/components/feature/floor-guide';
import { ChevronLeftIcon, ChevronRightIcon, CurrentLocationIcon, PaperIcon } from '@/components/icons';
import { NUMBER_OF_FLOORS_PER_VIEW_VERTICAL } from '@/constants/floor-guide';
import { useAccessibilityTTS, useDisableWheelEvent, useScreenSize } from '@/hooks';
import { useVerticalFloorManagement } from '@/hooks/floor-guide';
import { generateAllFloorsGuideWithTTS, generateFloorName, generateFloorNameWithTTS, getFloorLocalizedName, getFloorMapName } from '@/utils/floor-guide';
import clsx from 'clsx';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import styles from './styles.module.scss';
import type { FloorInfo } from '@/services/dto';
import { useConfigStore } from '@/stores';

type Props = {
    floors?: FloorInfo[];
    selectedFloorName?: string;
    isShowAllFloorGuide: boolean;
    onSelectFloor?: (floor: FloorInfo) => void;
    setIsShowAllFloorGuide: (isShow: boolean) => void;
    onShowMyLocation: () => void;
    onShowPoiList?: () => void;
    currentMapBuildingId?: string;
};

export const VerticalFloorList = ({ floors, selectedFloorName, onSelectFloor, isShowAllFloorGuide, onShowMyLocation, onShowPoiList }: Props) => {
    const { t, i18n } = useTranslation();
    const { scaleHeightPixel } = useScreenSize();
    const { speak } = useAccessibilityTTS();
    const buttonText = t('common.button.tts.button');
    const inactiveButtonText = t('common.button.tts.inactive_button');

    const constrast = useConfigStore((state) => state.config.ui.contrast);

    const [lastInteractionWasMouse, setLastInteractionWasMouse] = useState(false);
    const speakCleanupRef = useRef<(() => void) | null>(null);

    const sortedFloors = useMemo(() => {
        if (!Array.isArray(floors) || !floors.length) {
            return [];
        }

        return [...floors].sort((a, b) => a.order - b.order);
    }, [floors]);

    const cancelPendingSpeak = useCallback(() => {
        speakCleanupRef.current?.();
        speakCleanupRef.current = null;
    }, []);

    const scheduleSpeak = useCallback(
        (text: string) => {
            cancelPendingSpeak();
            speakCleanupRef.current = speak(text) ?? null;
        },
        [cancelPendingSpeak, speak]
    );

    const handleFloorActionMouseDown = () => {
        setLastInteractionWasMouse(true);
    };

    const handleMyLocationFocus = () => {
        if (!lastInteractionWasMouse) {
            scheduleSpeak(`${t('floor_guide.button.current_location')}, ${buttonText}`);
        }
        setLastInteractionWasMouse(false);
    };

    const handleMyLocationBlur = () => {
        cancelPendingSpeak();
    };

    const handleViewStoreListFocus = () => {
        if (!lastInteractionWasMouse) {
            scheduleSpeak(`${t('floor_guide.button.view_store_list')}, ${buttonText}`);
        }
        setLastInteractionWasMouse(false);
    };

    const isShowFloors = useMemo(() => Array.isArray(floors) && floors.length > 0, [floors]);

    // State to track current visible index of Swiper
    const [currentActiveIndex, setCurrentActiveIndex] = useState(0);

    const swiperContainerRef = useRef<HTMLDivElement>(null);

    // Use custom hook to disable wheel events on Swiper container
    useDisableWheelEvent(swiperContainerRef);

    const { handleScrollUp, handleScrollDown, shouldScroll, swiperRef, isDisabledPrev, isDisabledNext, handleSlideChange, scrollToSelectedFloor } =
        useVerticalFloorManagement(sortedFloors, NUMBER_OF_FLOORS_PER_VIEW_VERTICAL, selectedFloorName);

    const isSelectedItem = useCallback(
        (floor?: FloorInfo) => {
            if (!floor) {
                return false;
            }
            return getFloorMapName(floor) === selectedFloorName;
        },
        [selectedFloorName]
    );

    const handleSelectFloor = useCallback(
        (floor?: FloorInfo) => {
            if (!floor) {
                return;
            }
            const floorName = getFloorLocalizedName(floor, i18n.language);
            speak?.(t('floor_guide.text_to_speech.move_to_floor', { floorName: generateFloorName(floorName) }));
            onSelectFloor?.(floor);
        },
        [onSelectFloor, speak, t, i18n.language]
    );

    const handleFocusFloorBtn = useCallback(
        (floor: FloorInfo, selected: boolean) => {
            if (!floor) return;
            const floorName = getFloorLocalizedName(floor, i18n.language);
            speak(generateFloorNameWithTTS(t, t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floorName) }), selected));
        },
        [speak, t, i18n.language]
    );

    // Voice handling for arrow buttons
    const handleScrollUpWithTTS = useCallback(() => {
        if (isDisabledPrev) {
            speak(t('floor_guide.text_to_speech.click.inactive_prev_floor_btn'));
        } else {
            speak(t('floor_guide.text_to_speech.click.active_prev_floor_btn'));
            handleScrollUp();
        }
    }, [handleScrollUp, isDisabledPrev, speak, t]);

    const handleScrollDownWithTTS = useCallback(() => {
        if (isDisabledNext) {
            speak(t('floor_guide.text_to_speech.click.inactive_next_floor_btn'));
        } else {
            speak(t('floor_guide.text_to_speech.click.active_next_floor_btn'));
            handleScrollDown();
        }
    }, [handleScrollDown, isDisabledNext, speak, t]);

    // Calculate which floor items are visible based on activeIndex and slidesPerView
    const visibleFloorIndexes = useMemo(() => {
        if (!sortedFloors?.length) return [];

        const visibleIndexes = new Set<number>();
        const startIndex = currentActiveIndex;
        const endIndex = Math.min(startIndex + NUMBER_OF_FLOORS_PER_VIEW_VERTICAL, sortedFloors.length);

        for (let i = startIndex; i < endIndex; i++) {
            visibleIndexes.add(i);
        }

        return Array.from(visibleIndexes);
    }, [currentActiveIndex, sortedFloors?.length]);

    // Function to determine if a floor item should have tabIndex
    const getFloorTabIndex = useCallback(
        (floorIndex: number) => {
            return visibleFloorIndexes.includes(floorIndex) ? 8 : -1;
        },
        [visibleFloorIndexes]
    );

    return (
        <section
            className={clsx(styles['floor-list'], {
                [styles['floor-list--scrollable']]: shouldScroll,
            })}
        >
            {isShowFloors && (
                <section className={styles['floor-list__items']}>
                    {shouldScroll && (
                        <ArrowFloorButton
                            data-region-nav
                            data-type="button"
                            data-region-order={7}
                            data-tts-scope="arrow-floor-button"
                            tabIndex={9}
                            data-interactive
                            className="swiper-prev absolute left-0 z-10"
                            aria-disabled={isDisabledPrev}
                            onClick={() => handleScrollUpWithTTS()}
                            onFocus={() => {
                                if (isDisabledPrev) {
                                    speak(`${t('floor_guide.text_to_speech.focus.inactive_prev_floor_btn')} ${inactiveButtonText}`);
                                } else {
                                    speak(`${t('floor_guide.text_to_speech.focus.active_prev_floor_btn')} ${buttonText}`);
                                }
                            }}
                        >
                            <ChevronLeftIcon />
                        </ArrowFloorButton>
                    )}

                    <Swiper
                        mousewheel={false}
                        allowTouchMove={true}
                        simulateTouch={false}
                        preventInteractionOnTransition
                        ref={swiperRef}
                        modules={[Navigation]}
                        slidesPerView={NUMBER_OF_FLOORS_PER_VIEW_VERTICAL}
                        slidesPerGroup={1}
                        spaceBetween={scaleHeightPixel(8)}
                        navigation={{
                            prevEl: '.swiper-prev',
                            nextEl: '.swiper-next',
                        }}
                        loop={false}
                        onSlideChange={(swiper) => {
                            handleSlideChange(swiper.activeIndex);
                            setCurrentActiveIndex(swiper.activeIndex);
                        }}
                        onInit={(swiper) => {
                            setCurrentActiveIndex(swiper.activeIndex);
                            scrollToSelectedFloor();
                        }}
                        className="flex-1 overflow-hidden"
                        data-region-nav
                        data-region-order={6}
                    >
                        {sortedFloors?.map((floor, index) => (
                            <SwiperSlide key={floor.id} className="flex !w-auto items-center justify-center !mr-0">
                                <FloorButton
                                    data-tts-scope="floor-button"
                                    tabIndex={getFloorTabIndex(index)}
                                    data-interactive
                                    isSelected={isSelectedItem(floor)}
                                    onClick={() => handleSelectFloor(floor)}
                                    onFocus={() => handleFocusFloorBtn(floor, isSelectedItem(floor))}
                                >
                                    {getFloorLocalizedName(floor, i18n.language) || '-'}
                                </FloorButton>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    {shouldScroll && (
                        <ArrowFloorButton
                            data-tts-scope="arrow-floor-button"
                            className="swiper-next absolute right-0 z-10 w-[calc(56*var(--vh-unit))]"
                            aria-disabled={isDisabledNext}
                            data-type="button"
                            onClick={() => handleScrollDownWithTTS()}
                            tabIndex={10}
                            data-interactive
                            data-region-nav
                            data-region-order={7}
                            onFocus={() => {
                                if (isDisabledNext) {
                                    speak(`${t('floor_guide.text_to_speech.focus.inactive_next_floor_btn')} ${inactiveButtonText}`);
                                } else {
                                    speak(`${t('floor_guide.text_to_speech.focus.active_next_floor_btn')} ${buttonText}`);
                                }
                            }}
                        >
                            <ChevronRightIcon />
                        </ArrowFloorButton>
                    )}
                </section>
            )}
            <section className="flex flex-col gap-[calc(6*var(--vh-unit))] px-[calc(4*var(--vh-unit))]">
                <SecondaryFloorButton
                    data-region-nav
                    data-region-order={8}
                    tabIndex={11}
                    data-interactive
                    data-tts-scope="my-location-button"
                    onMouseDown={handleFloorActionMouseDown}
                    onFocus={handleMyLocationFocus}
                    onBlur={handleMyLocationBlur}
                    onClick={onShowMyLocation}
                    isSelected={isShowAllFloorGuide}
                    aria-label={generateAllFloorsGuideWithTTS(t, t('floor_guide.button.all_floors_guide'), isShowAllFloorGuide)}
                >
                    <span className="flex-shrink-0">
                        <CurrentLocationIcon />
                    </span>
                    {t('floor_guide.button.current_location')}
                </SecondaryFloorButton>
                <SecondaryFloorButton
                    role="button"
                    data-region-nav
                    data-region-order={9}
                    data-tts-scope="view-store-list-button"
                    tabIndex={12}
                    variant={constrast ? 'default' : 'gray'}
                    onMouseDown={handleFloorActionMouseDown}
                    onFocus={handleViewStoreListFocus}
                    onClick={() => onShowPoiList?.()}
                >
                    <span className="flex-shrink-0">
                        <PaperIcon />
                    </span>
                    {t('floor_guide.button.view_store_list')}
                </SecondaryFloorButton>
            </section>
        </section>
    );
};
